import { Link } from "react-router-dom";
import { ArrowRight, Eye, Scan, Droplets, Layers, Stethoscope, Globe2, BookOpen, Linkedin, Instagram, Facebook } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ProfPortrait } from "@/components/ProfPortrait";

const CTA_PRIMARY =
  "inline-flex items-center gap-2 rounded-full bg-[#E8F1EC] px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#0D3327] transition-colors duration-300 hover:bg-white";
const CTA_OUTLINE =
  "inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:border-white/70";
const CTA_DARK =
  "inline-flex items-center gap-2 rounded-full bg-[#0D3327] px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-[#174C3A]";

const STATS = [
  { value: "2009–2024", label: "Direttore Clinica Oculistica", sub: "U.O.C. Clinica Oculistica", sub2: "Ospedale Policlinico San Martino – Genova", testid: "stat-director" },
  { value: "2010–2024", label: "Professore Ordinario", sub: "Università degli Studi di Genova", testid: "stat-professor" },
  { value: "10.943+", label: "Citazioni scientifiche", sub: "Google Scholar", testid: "stat-citations" },
  { value: "52", label: "H-index", sub: "Google Scholar", testid: "stat-h-index" },
  { value: "175", label: "i10-index", sub: "Google Scholar", testid: "stat-i10-index" },
];

const AREAS = [
  { icon: Eye, title: "Chirurgia della cataratta", text: "Intervento di sostituzione del cristallino opacizzato con lente intraoculare, pianificato sul singolo paziente.", testid: "service-card-cataract" },
  { icon: Scan, title: "Chirurgia refrattiva", text: "Correzione di miopia, ipermetropia e astigmatismo con tecniche laser o sul cristallino.", testid: "service-card-refractive" },
  { icon: Droplets, title: "Chirurgia del glaucoma", text: "Diagnosi precoce, monitoraggio e interventi per il controllo della pressione intraoculare.", testid: "service-card-glaucoma" },
  { icon: Layers, title: "Chirurgia della cornea", text: "Trapianto corneale completo o selettivo dei soli strati interessati dalla patologia.", testid: "service-card-cornea" },
  { icon: Stethoscope, title: "Attività ambulatoriale", text: "Visita oculistica specialistica e diagnostica strumentale completa.", testid: "service-card-diagnostics" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="bg-[#0D3327]">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-16 lg:px-8 lg:py-24">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E8F1EC]/50">
              Genova · Savona
            </p>
            <h1 data-testid="hero-title" className="mt-5 font-serif text-4xl font-semibold leading-[1.1] text-white sm:text-5xl lg:text-6xl">
              Professor Carlo Enrico Traverso
            </h1>
            <div className="mt-5 flex items-center gap-3">
              <span className="h-px w-10 bg-[#2E6B50]" />
              <p data-testid="hero-tagline" className="text-sm font-semibold uppercase tracking-[0.22em] text-[#E8F1EC]/85">
                Chirurgo Oftalmico esperto
              </p>
            </div>
            <p data-testid="hero-location" className="mt-3 text-xs font-medium uppercase tracking-[0.2em] text-[#E8F1EC]/60 sm:text-sm">
              Oculista a Genova e Savona
            </p>
            <p data-testid="hero-subtitle" className="mt-5 font-serif text-xl italic text-[#E8F1EC]/85 sm:text-2xl">
              Oftalmologia e chirurgia del segmento anteriore
            </p>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
              Esperienza clinica, chirurgica e accademica maturata in oltre quattro decenni di attività in Italia e all'estero, con particolare attenzione alla chirurgia della cataratta, del glaucoma, della cornea e alla chirurgia refrattiva.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/richiedi-una-valutazione" data-testid="hero-cta-primary" className={CTA_PRIMARY}>
                Richiedi una valutazione <ArrowRight size={15} />
              </Link>
              <Link to="/chi-sono" data-testid="hero-cta-secondary" className={CTA_OUTLINE}>
                Scopri il mio percorso
              </Link>
            </div>
            <div data-testid="hero-social-links" className="mt-8 flex items-center gap-3">
              {[
                { icon: Linkedin, label: "LinkedIn – Professor Carlo Enrico Traverso", testid: "hero-social-linkedin", url: "https://www.linkedin.com/in/carlo-enrico-traverso-400174a/" },
                { icon: Instagram, label: "Instagram – Professor Carlo Enrico Traverso", testid: "hero-social-instagram", url: "https://www.instagram.com/professor_traverso_oculista_md/" },
                { icon: Facebook, label: "Facebook – Professor Carlo Enrico Traverso", testid: "hero-social-facebook", url: "https://www.facebook.com/profile.php?id=61572806454159" },
              ].map((s) =>
                s.url ? (
                  <a
                    key={s.testid}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={s.testid}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white/55 transition-colors duration-300 hover:border-[#E8F1EC]/70 hover:text-white"
                  >
                    <s.icon size={15} />
                  </a>
                ) : (
                  <span
                    key={s.testid}
                    aria-label={s.label}
                    data-testid={s.testid}
                    className="flex h-9 w-9 cursor-default items-center justify-center rounded-full border border-white/20 text-white/55 opacity-50"
                  >
                    <s.icon size={15} />
                  </span>
                )
              )}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative mx-auto w-full max-w-md">
              <ProfPortrait
                testid="hero-portrait-image"
                priority
                className="aspect-[4/5] w-full rounded-lg border border-[#E8F1EC]/20 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.45)]"
              />
              <div className="mt-4 text-center">
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#E8F1EC]/45">
                  Medico Chirurgo · Specialista in Oftalmologia
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRESENTAZIONE BREVE */}
      <section data-testid="intro-section" className="bg-[#F8FAF8]">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#2E6B50]">Presentazione</p>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-[#0D3327] sm:text-4xl">
              Esperienza, ricerca e innovazione nella cura della vista
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-5 text-base leading-relaxed text-[#4B5852] sm:text-lg">
              <p>
                Il Professor Carlo Enrico Traverso ha dedicato la propria attività clinica, chirurgica e scientifica alle patologie del segmento anteriore dell'occhio.
              </p>
              <p>
                Professore Ordinario di Oftalmologia dal 2010 al 2024 e Direttore della U.O.C. Clinica Oculistica dell'Ospedale Policlinico San Martino di Genova dal 2009 al 2024, ha affiancato per tutta la propria carriera attività assistenziale, chirurgia, ricerca scientifica e formazione universitaria.
              </p>
              <p>
                La sua esperienza riguarda in particolare chirurgia della cataratta, glaucoma, cornea, trapianto corneale e chirurgia refrattiva. Oggi svolge attività professionale a Genova e Savona presso Casa della Salute.
              </p>
              <Link to="/chi-sono" data-testid="intro-cta-about" className={CTA_DARK}>
                Conosci il mio percorso <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* NUMERI / HIGHLIGHTS */}
      <section data-testid="academic-stats-section" className="border-y border-[#D5E2DB] bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {STATS.map((s, i) => (
              <Reveal key={s.testid} delay={i * 0.06}>
                <div
                  data-testid={s.testid}
                  className="h-full rounded-lg border border-[#D5E2DB] bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[#2E6B50]/50 hover:shadow-md"
                >
                  <p className="font-mono text-xl font-medium text-[#174C3A] sm:text-2xl">{s.value}</p>
                  <p className="mt-3 text-sm font-semibold text-[#0D3327]">{s.label}</p>
                  <p className="mt-1 text-xs leading-relaxed text-[#4B5852]">
                    {s.sub}
                    {s.sub2 && (
                      <>
                        <br />
                        {s.sub2}
                      </>
                    )}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-center text-xs text-[#4B5852]/70">
            Dati Google Scholar aggiornati al 28 agosto 2025.
          </p>
        </div>
      </section>

      {/* ESPERIENZA INTERNAZIONALE */}
      <section data-testid="international-section" className="bg-[#E8F1EC]">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3">
              <Globe2 size={20} className="text-[#174C3A]" />
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#174C3A]">
                Esperienza internazionale
              </p>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <Reveal delay={0.05}>
              <div data-testid="intl-card-wills" className="h-full rounded-lg border border-[#174C3A]/15 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md sm:p-10">
                <p className="font-mono text-sm text-[#2E6B50]">Philadelphia · USA</p>
                <h3 className="mt-3 font-serif text-2xl font-semibold text-[#0D3327]">Wills Eye Hospital</h3>
                <p className="mt-4 text-base leading-relaxed text-[#4B5852]">
                  Due anni di perfezionamento e formazione specialistica presso uno dei centri oftalmologici più prestigiosi al mondo.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div data-testid="intl-card-kkesh" className="h-full rounded-lg border border-[#174C3A]/15 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md sm:p-10">
                <p className="font-mono text-sm text-[#2E6B50]">Riyadh · Arabia Saudita</p>
                <h3 className="mt-3 font-serif text-2xl font-semibold text-[#0D3327]">King Khaled Eye Specialist Hospital</h3>
                <p className="mt-4 text-base leading-relaxed text-[#4B5852]">
                  Senior Staff per 3,5 anni, con particolare attività nell'ambito dei casi complessi di chirurgia del segmento anteriore.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* AREE DI ATTIVITÀ */}
      <section data-testid="services-section" className="bg-[#F8FAF8]">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#2E6B50]">Aree di attività</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold leading-tight text-[#0D3327] sm:text-4xl">
              Di cosa mi occupo
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {AREAS.map((a, i) => (
              <Reveal key={a.testid} delay={i * 0.05} className={i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <div
                  data-testid={a.testid}
                  className="group flex h-full flex-col rounded-lg border border-[#D5E2DB] bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[#2E6B50]/50 hover:shadow-md"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E8F1EC] text-[#174C3A] transition-colors duration-300 group-hover:bg-[#174C3A] group-hover:text-white">
                    <a.icon size={20} />
                  </div>
                  <h3 className="mt-6 font-serif text-xl font-semibold text-[#0D3327]">{a.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-[#4B5852]">{a.text}</p>
                  <Link
                    to="/di-cosa-mi-occupo"
                    data-testid={`${a.testid}-link`}
                    className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#174C3A] transition-colors duration-300 hover:text-[#0D3327]"
                  >
                    Scopri di più <ArrowRight size={14} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RICERCA */}
      <section data-testid="research-section" className="border-y border-[#D5E2DB] bg-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-16 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3">
              <BookOpen size={20} className="text-[#2E6B50]" />
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#2E6B50]">Attività scientifica</p>
            </div>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-[#0D3327] sm:text-4xl">
              Ricerca scientifica e innovazione
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-[#4B5852] sm:text-lg">
              <p>
                Una parte centrale dell'attività scientifica del Professor Traverso è stata dedicata al glaucoma e alla superficie oculare, con particolare rilievo per gli studi sulla ricostituzione e sul trapianto delle cellule staminali limbari corneali.
              </p>
              <p>
                La ricerca sulla terapia cellulare corneale ha contribuito alle prime dimostrazioni cliniche nell'uomo dell'impiego delle cellule staminali per la ricostruzione della superficie corneale, con lavori pubblicati anche su The Lancet.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="rounded-lg border border-[#D5E2DB] bg-[#F8FAF8] p-8 sm:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2E6B50]">Pubblicazioni</p>
              <div className="mt-6 space-y-5">
                {[
                  ["Glaucoma", "Ricerca clinica e chirurgica sul nervo ottico"],
                  ["Cellule staminali limbari", "Tra le prime dimostrazioni cliniche nell'uomo"],
                ].map(([t, d]) => (
                  <div key={t} className="border-l-2 border-[#2E6B50]/40 pl-4">
                    <p className="text-sm font-semibold text-[#0D3327]">{t}</p>
                    <p className="mt-1 text-xs leading-relaxed text-[#4B5852]">{d}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 border-t border-[#D5E2DB] pt-6">
                <p className="font-serif text-lg italic text-[#174C3A]">
                  "Lavori pubblicati anche su The Lancet."
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA FINALE */}
      <section data-testid="home-final-cta" className="bg-[#0D3327]">
        <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 sm:py-28 lg:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl font-semibold leading-tight text-white sm:text-4xl">
              Hai bisogno di una valutazione specialistica?
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
              Descrivi brevemente il tuo problema. Sarai ricontattato per valutare il percorso più appropriato.
            </p>
            <Link to="/richiedi-una-valutazione" data-testid="home-final-cta-button" className={`${CTA_PRIMARY} mt-9`}>
              Richiedi una valutazione <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
